/** Single animated bar — supports negative values for margin charts */

import { barSpan, plotFraction } from '@parlay-picker/shared/chartScale';
import { teamLineOutcome } from '@parlay-picker/shared/lineOutcome';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { type CSSProperties, type ReactElement, useState } from 'react';
import { OUTCOME_STYLE, outcomeGradient } from '../stats-chart/outcomeStyles';
import type { ChartDataPoint } from './types';

/** A bar is at least this percent of the plot's height */
const MIN_BAR_PERCENT = 2;

interface TeamAnimatedBarProps {
  point: ChartDataPoint;
  maxValue: number;
  minValue: number;
  propLine: number;
  propType: string;
  isUnderBet: boolean;
  isExpanded: boolean;
  onClick: () => void;
  index: number;
  barCount: number;
}

export function TeamAnimatedBar({
  point,
  maxValue,
  minValue,
  propLine,
  propType,
  isUnderBet,
  isExpanded,
  onClick,
  index,
  barCount,
}: TeamAnimatedBarProps): ReactElement {
  const [showTooltip, setShowTooltip] = useState(false);
  const outcome = teamLineOutcome(point, propType, propLine, isUnderBet);

  // Bars grow from the zero line, so a negative margin hangs below it
  const span = barSpan(point.value, minValue, maxValue);
  const barHeightPercent = Math.max(span.height * 100, MIN_BAR_PERCENT);

  const glowColor = OUTCOME_STYLE[outcome].glow;
  const barStyle: CSSProperties = {
    minHeight: '4px',
    background: outcomeGradient(outcome),
    // Mute bars that are using full-game fallback data instead of period data
    ...(point.hasPeriodData === false && { opacity: 0.35 }),
  };

  return (
    <div
      className={cn(
        'relative flex-1 min-w-0 cursor-pointer group',
        barCount > 10 ? 'max-w-8' : 'max-w-12',
      )}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Value label — absolute above bar area */}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.04 + 0.3 }}
        className={cn(
          'absolute -top-3.5 left-1/2 -translate-x-1/2 text-[9px] font-bold leading-none whitespace-nowrap z-10',
          OUTCOME_STYLE[outcome].text,
        )}
      >
        {propType === 'total' ? point.value : point.value > 0 ? `+${point.value}` : point.value}
      </motion.span>

      {/* Hover tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full mb-0 z-50 pointer-events-none"
          >
            <div className="bg-theme-surface border border-theme-border rounded-lg shadow-xl px-3 py-2 min-w-35">
              {/* Date */}
              <div className="text-xs font-medium text-theme-text mb-1">
                {point.dateFormatted || point.label}
              </div>
              {/* Opponent with home/away indicator */}
              {point.opponent && (
                <div className="text-xs text-theme-text-secondary mb-1">
                  <span className="font-semibold">{point.isHome ? 'vs' : '@'}</span>{' '}
                  {point.opponent}
                </div>
              )}
              {/* Score with W/L */}
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={cn(
                    'font-bold',
                    point.won ? 'text-theme-success' : 'text-theme-danger',
                  )}
                >
                  {point.won ? 'W' : 'L'}
                </span>
                <span className="text-theme-text-secondary">
                  {point.teamScore}-{point.opponentScore}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bar — fills the full flex item height so percentages match the grid area */}
      <motion.div
        initial={{
          height: 0,
          bottom: `${plotFraction(0, minValue, maxValue) * 100}%`,
        }}
        animate={{
          height: `${barHeightPercent}%`,
          bottom: `${span.bottom * 100}%`,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25,
          delay: index * 0.04,
        }}
        className={cn(
          'w-full absolute rounded-sm transition-shadow',
          isExpanded ? `shadow-lg ${glowColor}` : '',
        )}
        style={barStyle}
      />

      {/* Home/away dot + X-axis label — absolute below bar area */}
      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0">
        <span
          className={cn(
            'w-1 h-1 rounded-full',
            point.isHome ? 'bg-theme-primary' : 'bg-theme-text-tertiary',
          )}
        />
        <span
          className={cn(
            'text-[9px] text-theme-text-tertiary leading-none whitespace-nowrap text-center',
            barCount > 5 && 'text-[8px]',
          )}
        >
          {barCount > 5 ? point.compactLabel : point.label}
        </span>
      </div>
    </div>
  );
}
